import React, { useState } from "react";
import {
  animations,
  styles,
  Hero,
  SectionHeader,
  CTASection,
} from "../../../components/landing/SharedComponent";
import { motion } from "framer-motion";
import ServiceDetail from "./ServiceDetail";
import { services, engineeringServices, materialTypes } from "./ServiceData";

const Service = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleServiceSwitch = (newService) => {
    setSelectedService(newService);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero
        title="Our Services"
        subtitle="Comprehensive Solutions for Industrial Protection"
        backgroundImage="/images/landing/bg.png"
      />

      {/* Main Services Section - Desktop */}
      <div
        className={`${styles.container} ${styles.sectionSpacing} hidden md:block`}
      >
        <div className="grid grid-cols-6 gap-8">
          {services.map((service) => (
            <motion.div
              key={service.id}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={animations.fadeInUp}
              className={`relative rounded-lg overflow-hidden ${styles.shadowBase} cursor-pointer group`}
              style={{
                gridArea: service.gridArea,
                height: "420px",
              }}
              onClick={() => handleServiceClick(service)}
            >
              <img
                src={service.mainImage}
                alt={service.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20 group-hover:from-black/95 transition-all duration-300" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-4 mb-3 transform group-hover:translate-y-0 translate-y-2 transition-transform duration-300">
                  <div className="text-4xl text-white">{service.icon}</div>
                  <h2 className="text-2xl font-bold text-white">
                    {service.title}
                  </h2>
                </div>
                <p className="text-gray-200 text-sm opacity-90 transform group-hover:translate-y-0 translate-y-2 transition-transform duration-300">
                  {service.description}
                </p>
                {/* <div className="mt-4 overflow-hidden h-0 group-hover:h-8 transition-all duration-300">
                  <span className="inline-block px-4 py-1 bg-primary/90 text-white text-sm rounded-full">
                    View Details
                  </span>
                </div> */}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Services Section - Mobile */}
      <div className={`${styles.container} ${styles.sectionSpacing} md:hidden`}>
        <div className="space-y-8 flex flex-col">
          {services.map((service) => (
            <motion.div
              key={service.id}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={animations.fadeInUp}
              className={`bg-white rounded-lg overflow-hidden ${styles.shadowBase} cursor-pointer hover:shadow-lg transition-shadow duration-300`}
              onClick={() => handleServiceClick(service)}
            >
              <div className="flex flex-col">
                <div className="relative">
                  <img
                    src={service.mainImage}
                    alt={service.title}
                    className="w-full h-56 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <div className="p-4 text-white">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-2xl">{service.icon}</div>
                        <h2 className="text-xl font-bold">{service.title}</h2>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-gray-600 text-sm">{service.description}</p>
                  <div className="mt-3 text-primary font-medium text-sm">
                    Learn more →
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ServiceDetail
        service={selectedService}
        services={services}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onServiceSwitch={handleServiceSwitch}
      />

      {/* Engineering Services Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`bg-white ${styles.sectionSpacing}`}
      >
        <div className={styles.container}>
          <SectionHeader title="Engineering Services" className="mb-16" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {engineeringServices.map((service, idx) => (
              <div
                key={idx}
                className={`
                  bg-gray-50 p-6 rounded-lg 
                  hover:shadow-md hover:bg-gray-100 ${styles.transitionBase}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-primary rounded-full" />
                  <p className="text-gray-700">{service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Materials Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`bg-gray-50 ${styles.sectionSpacing}`}
      >
        <div className={styles.container}>
          <SectionHeader title="Material Types" className="mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 py-12">
            {materialTypes.map((material, idx) => (
              <div
                key={idx}
                className={`
                  bg-white rounded-lg overflow-hidden group
                  ${styles.shadowBase} hover:shadow-md transition-shadow duration-300
                `}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={material.image}
                    alt={material.name}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {material.name}
                  </h3>
                  {material.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {material.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <CTASection
        title="Need Our Services?"
        subtitle="Contact us today to discuss your project requirements"
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </div>
  );
};

export default Service;
